import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";

/**
 * Consistent top bar for every page.
 *
 * - Theme-aware translucent background (light in light mode, dark in dark mode).
 * - Sticky, with subtle backdrop blur.
 * - Impo wordmark sits on the left and doubles as the go-home button.
 * - Page title/subtitle sits centered next to the wordmark.
 * - Right slot supports optional Search icon, always-on theme toggle, and a
 *   `right` node for page actions.
 */
export default function TopBar({
  title,
  subtitle,
  back = false,
  right = null,
  showSearch = false,
  hideTheme = false,
  // Legacy props accepted but ignored so existing pages don't have to change.
  showHome: _showHome,
  showLogo: _showLogo,
  showTheme: _showTheme,
}) {
  const nav = useNavigate();
  return (
    <div
      className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border"
      data-testid="top-bar"
    >
      <div className="flex items-center gap-2 px-4 py-3 min-h-[58px]">
        {back && (
          <button
            onClick={() => nav(-1)}
            className="h-9 w-9 -ml-2 rounded-full flex items-center justify-center hover:bg-[hsl(var(--accent))] active:scale-95 transition text-foreground"
            aria-label="Back"
            data-testid="top-bar-back-button"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {/* Wordmark on the left. Always visible; tap to jump to Home (Chat). */}
        <button
          onClick={() => nav("/app/chat")}
          className="inline-flex items-center hover:opacity-90 active:scale-95 transition shrink-0"
          aria-label="Go to home"
          data-testid="top-bar-logo"
        >
          <Logo size={22} />
        </button>

        {/* Title / subtitle in the remaining space */}
        <div className="flex-1 min-w-0 ml-2">
          {title && (
            <div
              className="font-display font-semibold text-[15px] leading-tight truncate text-foreground"
              data-testid="top-bar-title"
            >
              {title}
            </div>
          )}
          {subtitle && (
            <div className="text-[11px] leading-tight truncate text-muted-foreground">
              {subtitle}
            </div>
          )}
        </div>

        {showSearch && (
          <button
            onClick={() => nav("/app/search")}
            className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[hsl(var(--accent))] active:scale-95 transition text-foreground"
            aria-label="Search"
            data-testid="top-bar-search-button"
          >
            <Search className="h-5 w-5" />
          </button>
        )}
        {!hideTheme && <ThemeToggle size="sm" />}
        {right}
      </div>
    </div>
  );
}
